import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import CountUp from "react-countup";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import Countdown from "@/components/Countdown";
import AuthDialog from "@/components/AuthDialog";

const GANDHI_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/portrait.jpg`;
const HERO_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/hero.jpg`;

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const heroRef = useRef(null);
  const gandhiRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(heroProgress, [0, 1], [0, 140]);
  const heroTextY = useTransform(heroProgress, [0, 1], [0, -60]);

  const { scrollYProgress: gandhiProgress } = useScroll({ target: gandhiRef, offset: ["start end", "end start"] });
  const gandhiImgY = useTransform(gandhiProgress, [0, 1], [-60, 60]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const handleBuy = () => {
    if (user) navigate("/grid");
    else setAuthOpen(true);
  };

  return (
    <main className="bg-[#FAF8F5] text-[#3C3735] overflow-x-hidden">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end border-b border-border/60 pt-24">
        <div className="absolute inset-0 overflow-hidden">
          <motion.img
            src={HERO_IMG}
            alt={t("alts.hero")}
            style={{ y: heroImgY }}
            className="absolute right-0 -top-[6%] h-[112%] w-[62%] object-cover object-center [mask-image:linear-gradient(to_left,black_55%,transparent_92%)]"
          />
        </div>
        <div className="relative z-10 px-5 md:px-12 pb-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-[10px] md:text-xs tracking-[0.4em] text-golddark mb-8"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.p>
          <motion.h1
            style={{ y: heroTextY }}
            className="font-display font-black tracking-tighter leading-[0.9] text-[13vw] md:text-[10vw]"
            data-testid="hero-title"
          >
            <MaskedLine delay={0.35}>{t("hero.line1")}</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-golddark">{t("hero.line2")}</span>
            </MaskedLine>
          </motion.h1>
          <MaskedLine delay={0.7}>
            <span className="font-display italic text-2xl md:text-4xl text-golddark tracking-tight" data-testid="hero-tagline">
              {t("hero.tagline")}
            </span>
          </MaskedLine>
          <div className="mt-10 grid md:grid-cols-2 gap-10 items-end">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t("hero.sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
              className="flex flex-wrap gap-4 md:justify-end"
            >
              <button
                onClick={handleBuy}
                data-testid="hero-cta-buy"
                className="px-8 py-4 bg-[#7D8C77] text-[#FAF8F5] text-xs tracking-[0.2em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
              >
                {t("hero.ctaBuy").toUpperCase()}
              </button>
              <Link
                to="/grid"
                data-testid="hero-cta-grid"
                className="px-8 py-4 bg-[#FAF8F5] border border-[#3C3735]/40 text-xs tracking-[0.2em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
              >
                {t("hero.ctaGrid").toUpperCase()}
              </Link>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="relative z-10 border-t border-border/60 px-5 md:px-12 py-4 flex justify-between text-[10px] tracking-[0.35em] text-muted-foreground"
        >
          <span>500.000 PX · $1 / PX</span>
          <span className="animate-pulse">{t("hero.scroll")} ↓</span>
        </motion.div>
      </section>

      {/* MISSIONE */}
      <section className="px-5 md:px-12 py-24 md:py-32 border-b border-border/60" data-testid="mission-section">
        <Reveal>
          <h2 className="font-display font-black tracking-tighter leading-[0.98] text-4xl sm:text-5xl lg:text-6xl max-w-5xl" data-testid="mission-kicker">
            {t("mission.kicker")}
          </h2>
          <p className="mt-8 text-sm md:text-base text-muted-foreground leading-loose max-w-2xl whitespace-pre-line">
            {t("mission.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-14 border-l-2 border-gold pl-6 md:pl-10 max-w-3xl" data-testid="mission-charity">
            <p className="text-sm md:text-base leading-relaxed text-[#3C3735]">{t("mission.charity")}</p>
          </div>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-4" data-testid="mission-counter">
            <div className="font-mono text-4xl md:text-5xl font-light tabular-nums text-golddark" data-testid="mission-counter-value">
              <CountUp end={stats?.sold ?? 0} duration={2.2} separator="." preserveValue />
            </div>
            <p className="text-[10px] tracking-[0.3em] text-muted-foreground leading-relaxed max-w-sm">
              {t("mission.counterLabel").toUpperCase()}
            </p>
            <Link
              to="/missione"
              data-testid="mission-more-link"
              className="text-[10px] tracking-[0.3em] text-golddark border-b border-gold/50 pb-1 hover:text-[#3C3735] transition-colors duration-200"
            >
              {t("mission.moreLink").toUpperCase()} →
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-16 space-y-2 font-display font-black tracking-tighter leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
            <p>{t("mission.line1")}</p>
            <p className="text-golddark">{t("mission.line2")}</p>
            <p>{t("mission.line3")}</p>
          </div>
          <p className="mt-8 text-xs md:text-sm tracking-[0.2em] text-muted-foreground">{t("mission.gesture").toUpperCase()}</p>
          <p className="mt-12 text-[10px] tracking-[0.4em] text-golddark">HUMANITY MOSAIC — {t("mission.signature").toUpperCase()}</p>
        </Reveal>
      </section>

      {/* TICKER */}
      <div className="border-b border-border/60 bg-[#FFFFFF] py-3" data-testid="marquee-ticker">
        <Marquee speed={40} gradient={false} pauseOnHover>
          <span className="text-[10px] tracking-[0.3em] text-golddark mx-8">{t("ticker.label")} —</span>
          {stats && stats.recent.length > 0 ? (
            stats.recent.map((r, i) => (
              <span key={i} className="text-[10px] tracking-[0.25em] text-muted-foreground mx-8">
                PIXEL [{r.x},{r.y}] — {(r.user_name || t("grid.anonymous")).toUpperCase()} ·
              </span>
            ))
          ) : (
            <span className="text-[10px] tracking-[0.25em] text-muted-foreground mx-8">{t("ticker.empty")} ·</span>
          )}
        </Marquee>
      </div>

      {/* STATS */}
      <section className="grid grid-cols-1 md:grid-cols-2 border-b border-border/60" data-testid="stats-section">
        {[
          [stats?.sold ?? 0, t("stats.sold"), "stats-sold-counter"],
          [stats ? stats.total - stats.sold : 500000, t("stats.remaining"), "stats-remaining-counter"],
        ].map(([val, label, id], i) => (
          <Reveal key={id} delay={i * 0.12} className={`px-5 md:px-12 py-14 ${i > 0 ? "border-t md:border-t-0 md:border-l border-border/60" : ""}`}>
            <div className="font-mono text-5xl md:text-6xl font-light tabular-nums" data-testid={id}>
              <CountUp end={val} duration={2.2} separator="." preserveValue />
            </div>
            <div className="mt-3 text-[10px] tracking-[0.35em] text-muted-foreground">{label.toUpperCase()}</div>
          </Reveal>
        ))}
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="px-5 md:px-12 py-28 md:py-40 border-b border-border/60" data-testid="manifesto-section">
        <Reveal>
          <p className="text-[10px] tracking-[0.4em] text-golddark mb-6">{t("manifesto.eyebrow")}</p>
          <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl whitespace-pre-line max-w-4xl">
            {t("manifesto.title")}
          </h2>
        </Reveal>
        <div className="mt-20 columns-1 md:columns-3 gap-12">
          {[
            ["01", t("manifesto.ch1t"), t("manifesto.ch1")],
            ["02", t("manifesto.ch2t"), t("manifesto.ch2")],
            ["03", t("manifesto.ch3t"), t("manifesto.ch3")],
          ].map(([num, title, body], i) => (
            <Reveal key={num} delay={i * 0.15} className="break-inside-avoid mb-12">
              <div className="border border-border/60 p-8 hover:border-gold/60 transition-colors duration-300">
                <div className="font-mono text-golddark text-xs tracking-[0.3em] mb-4">{num}</div>
                <h3 className="font-display text-2xl font-bold tracking-tight mb-4">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed drop-cap">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GANDHI */}
      <section ref={gandhiRef} className="relative px-5 md:px-12 py-28 md:py-40 border-b border-border/60 overflow-hidden" data-testid="gandhi-section">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] text-golddark mb-6">{t("gandhi.eyebrow")}</p>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl whitespace-pre-line">
              {t("gandhi.title")}
            </h2>
            <p className="mt-8 text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg">{t("gandhi.body")}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="relative border border-border/60 p-3">
              <motion.img
                src={GANDHI_IMG}
                alt={t("alts.gandhi")}
                style={{ y: gandhiImgY }}
                className="w-full aspect-[4/5] object-cover contrast-105 saturate-[0.9] scale-110"
                data-testid="gandhi-portrait"
              />
              <div className="absolute inset-3 border border-gold/30 pointer-events-none" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* COUNTDOWN + CTA */}
      <section className="px-5 md:px-12 py-28 md:py-40 text-center border-b border-border/60" data-testid="countdown-section">
        <Reveal>
          <p className="text-[10px] tracking-[0.4em] text-muted-foreground mb-10">{t("countdown.label")}</p>
          <Countdown />
          <div className="mt-16">
            <button
              onClick={handleBuy}
              data-testid="cta-buy-bottom"
              className="px-10 py-5 bg-[#7D8C77] text-[#FAF8F5] text-xs tracking-[0.25em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
            >
              {t("hero.ctaBuy").toUpperCase()}
            </button>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="px-5 md:px-12 py-10 flex flex-col md:flex-row gap-4 justify-between text-[10px] tracking-[0.3em] text-muted-foreground">
        <span>{t("footer.tagline").toUpperCase()}</span>
        <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-golddark transition-colors duration-200">
          PRIVACY
        </a>
        <span className="text-golddark">{t("footer.rights").toUpperCase()}</span>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => navigate("/grid")} />
    </main>
  );
}
